import { FC, useEffect } from 'react';
import { useSetRecoilState } from 'recoil';

import { FlowTileTotalsState } from 'views/complex-onboarding-flow/stores/states';

// __simplici_tile_totals__
// Imported here rather than from a folder index: custom-node imports the tile
// components by file path, which already meant one stylesheet never loaded.
import './tile-total.scss';

interface ITileTotal {
	/** Resolved price for the whole tile. Nothing renders at <= 0. */
	amount?: number;
	/** What the price is per -- "Verification" everywhere except Sign Agreement. */
	unit?: string;
	/**
	 * __simplici_accreditation_price_range__
	 * Upper bound, when the cost is not decided until the session runs (the
	 * investor picks their accreditation level). Given this, the tile renders a
	 * range instead of a single figure. Omitted everywhere else.
	 */
	max?: number;
	/**
	 * __simplici_flow_total__
	 * The node this tile belongs to. Given it, the tile reports its figure to
	 * FlowTileTotalsState and the footer's flow total picks it up. Omitted, the
	 * tile renders exactly as before and contributes nothing -- so a tile that
	 * has not been wired up cannot silently count twice or count wrong.
	 */
	nodeId?: string;
}

/**
 * The Total line on a step tile. Deliberately renders nothing when the amount is
 * zero, missing, or not yet resolved: a free step should stay clean, and a tile
 * still waiting on /pipeline-init must not claim to cost $0.00.
 */
export const TileTotal: FC<ITileTotal> = ({
	amount,
	unit = 'Verification',
	max,
	nodeId,
}) => {
	const value = Number(amount ?? 0);
	const upper = Number(max ?? 0);
	// Only a genuinely higher bound makes it a range; an equal or missing `max`
	// falls through to the single-figure rendering every other tile uses.
	const isRange = Number.isFinite(upper) && upper > value;
	const setFlowTotals = useSetRecoilState(FlowTileTotalsState);

	// __simplici_flow_total__: publish what this tile says, so the footer adds up
	// the figures on screen rather than pricing the flow a second time. The
	// cleanup is what keeps it honest -- a deleted node, or a step left behind by
	// navigating to Delivery Method, unmounts its tile and drops out of the sum.
	useEffect(() => {
		if (!nodeId) return;
		const contribution = Number.isFinite(value) && value > 0 ? value : 0;
		setFlowTotals(prev => ({
			...prev,
			[nodeId]: {
				min: contribution,
				...(isRange ? { max: upper } : {}),
			},
		}));
		return () => {
			setFlowTotals(prev => {
				if (!(nodeId in prev)) return prev;
				const next = { ...prev };
				delete next[nodeId];
				return next;
			});
		};
	}, [isRange, nodeId, setFlowTotals, upper, value]);

	if (!Number.isFinite(value) || value <= 0) return null;
	return (
		<div className="tile-total">
			<div className="tile-total__label">Total</div>
			<div className="tile-total__value">
				${value.toFixed(2)}
				{isRange && <> &ndash; ${upper.toFixed(2)}</>}
				<span className="tile-total__unit">/{unit}</span>
			</div>
		</div>
	);
};
